import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { INVITATION_ROLES, INVITATION_STATUSES } from "@/shared/invitations/constants";
import {
  hasActiveFilters,
  type InvitationsFilters as Filters,
} from "@/shared/invitations/build-invitations-query";
import {
  normalizeOrganizationId,
  ORGANIZATION_ID_MAX_LENGTH,
} from "@/shared/organizations/organization-id";
import type { InvitationRole, InvitationStatus } from "@/shared/invitations/types";

interface InvitationsFiltersProps {
  readonly filters: Filters;
  readonly onChange: (next: Filters) => void;
  readonly onReset: () => void;
}

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
const LABEL_CLASS = "font-sans text-sm font-medium text-foreground";

/**
 * Status and role apply on change. The organization id is applied on submit
 * (Enter / Apply) rather than per keystroke: the endpoint is audited and
 * throttled to 20 requests a minute, and a half-typed id matches nothing.
 * There is deliberately NO email search (invitee emails are PII).
 */
export function InvitationsFilters({ filters, onChange, onReset }: InvitationsFiltersProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(filters.organizationId);

  const submitOrganization = (event: FormEvent) => {
    event.preventDefault();
    onChange({ ...filters, organizationId: normalizeOrganizationId(draft) ?? "" });
  };

  const reset = () => {
    setDraft("");
    onReset();
  };

  return (
    <form
      role="search"
      aria-label={t("invitations.filters.label")}
      data-testid="invitations-filters"
      onSubmit={submitOrganization}
      className="flex flex-wrap items-end gap-3"
    >
      <div className="space-y-1.5">
        <label htmlFor="invitations-status" className={LABEL_CLASS}>
          {t("invitations.filters.status")}
        </label>
        <select
          id="invitations-status"
          data-testid="invitations-filter-status"
          value={filters.status ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              status: e.target.value === "" ? null : (e.target.value as InvitationStatus),
            })
          }
          className={SELECT_CLASS}
        >
          <option value="">{t("invitations.filters.statusAll")}</option>
          {INVITATION_STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`invitations.status.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="invitations-role" className={LABEL_CLASS}>
          {t("invitations.filters.role")}
        </label>
        <select
          id="invitations-role"
          data-testid="invitations-filter-role"
          value={filters.role ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              role: e.target.value === "" ? null : (e.target.value as InvitationRole),
            })
          }
          className={SELECT_CLASS}
        >
          <option value="">{t("invitations.filters.roleAll")}</option>
          {INVITATION_ROLES.map((value) => (
            <option key={value} value={value}>
              {t(`invitations.role.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="min-w-[14rem] space-y-1.5">
        <label htmlFor="invitations-organization" className={LABEL_CLASS}>
          {t("invitations.filters.organization")}
        </label>
        <Input
          id="invitations-organization"
          data-testid="invitations-filter-organization"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={ORGANIZATION_ID_MAX_LENGTH}
          placeholder={t("invitations.filters.organizationPlaceholder")}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <Button type="submit" data-testid="invitations-filter-apply" variant="secondary">
        {t("invitations.filters.apply")}
      </Button>
      {(hasActiveFilters(filters) || draft.trim().length > 0) && (
        <Button
          type="button"
          data-testid="invitations-filters-reset"
          variant="outline"
          onClick={reset}
        >
          {t("invitations.filters.reset")}
        </Button>
      )}
    </form>
  );
}
